import type { TicketComment } from '../types';

/**
 * Normalise a ticket's `recentComments` into structured entries.
 *
 * The canonical storage form (backend/app/workers/sync.py and the demo
 * seeder) is an array of `{ author, text, createdAt }`. Documents synced
 * before 2026-08-15 still carry the legacy joined-string form — one
 * comment per line with a `[Author]: body` prefix — which persists until
 * the next provider sync rewrites the ticket. This is the ONLY place that
 * legacy convention is allowed to be parsed; presentation code must never
 * see a bracketed author prefix.
 */
const LEGACY_LINE = /^\[(.+?)\]:\s?(.*)$/s;

export function normalizeComments(
  raw: TicketComment[] | string | null | undefined,
): TicketComment[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .filter(
        (c): c is TicketComment =>
          !!c && typeof c === 'object' && typeof (c as TicketComment).text === 'string',
      )
      .map((c) => ({ author: c.author ?? null, text: c.text, createdAt: c.createdAt ?? null }))
      .filter((c) => c.text.trim().length > 0);
  }
  return String(raw)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(LEGACY_LINE);
      return m
        ? { author: m[1], text: m[2], createdAt: null }
        : { author: null, text: line, createdAt: null };
    })
    .filter((c) => c.text.trim().length > 0);
}
